import { UploadOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Empty, Popconfirm, Space, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import { logoUrl, removeLogo, uploadLogo } from './api';

// Mirrors apps/api/src/firms/logo.constants.ts — the frontend can't import
// backend code, so these are kept in sync by hand. Client-side rejection is
// a UX nicety only; the server enforces the same limits authoritatively
// (fileFilter + multer's `limits.fileSize` on `PUT /firms/:id/logo`).
const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];
const MAX_LOGO_SIZE_BYTES = 1024 * 1024;

/** "Logo" tab (edit mode only). `hasLogo` comes from the parent's
 * `FirmDetail.logoMime` (truthy iff a logo is stored) — the actual bytes are
 * only ever fetched by the `<img>` tag hitting `GET /firms/:id/logo`
 * directly, never through this component's own state. */
export function FirmaLogoTab({ firmaId, hasLogo }: { firmaId: string; hasLogo: boolean }) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [pendingFile, setPendingFile] = useState<RcFile | null>(null);
  // Bumped after every successful upload/delete so the <img src> changes —
  // otherwise the browser would keep showing its cached image for the same
  // firm id even though the underlying bytes changed.
  const [version, setVersion] = useState(() => Date.now());
  // Bumped alongside `version` and used as the Upload's `key`, forcing it to
  // remount and drop its internal (uncontrolled) file list after a
  // successful upload.
  const [uploadKey, setUploadKey] = useState(0);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['firms'] });

  const uploadMutation = useMutation({
    mutationFn: (file: RcFile) => uploadLogo(firmaId, file),
    onSuccess: () => {
      message.success('Logo-ul a fost încărcat.');
      setPendingFile(null);
      setUploadKey((key) => key + 1);
      setVersion(Date.now());
      invalidate();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Logo-ul nu a putut fi încărcat.');
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => removeLogo(firmaId),
    onSuccess: () => {
      message.success('Logo-ul a fost șters.');
      setVersion(Date.now());
      invalidate();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Logo-ul nu a putut fi șters.');
    },
  });

  return (
    <div>
      {hasLogo ? (
        <div style={{ marginBottom: 16 }}>
          <img
            src={logoUrl(firmaId, version)}
            alt="Logo firmă"
            style={{ maxWidth: '100%', maxHeight: 160, display: 'block', marginBottom: 12 }}
          />
          <Popconfirm title="Ștergi logo-ul?" onConfirm={() => removeMutation.mutate()}>
            <Button danger loading={removeMutation.isPending}>
              Șterge logo
            </Button>
          </Popconfirm>
        </div>
      ) : (
        <Empty description="Firma nu are logo încărcat." style={{ marginBottom: 16 }} />
      )}

      <Space direction="vertical">
        <Upload
          key={uploadKey}
          accept="image/png,image/jpeg,image/svg+xml"
          maxCount={1}
          beforeUpload={(file) => {
            if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
              message.error('Fișierul trebuie să fie PNG, JPEG sau SVG.');
              return Upload.LIST_IGNORE;
            }
            if (file.size > MAX_LOGO_SIZE_BYTES) {
              message.error('Fișierul depășește dimensiunea maximă permisă (1MB).');
              return Upload.LIST_IGNORE;
            }
            setPendingFile(file);
            return false;
          }}
          onRemove={() => {
            setPendingFile(null);
            return true;
          }}
        >
          <Button icon={<UploadOutlined />}>Selectează fișier</Button>
        </Upload>
        <Button
          type="primary"
          disabled={!pendingFile}
          loading={uploadMutation.isPending}
          onClick={() => pendingFile && uploadMutation.mutate(pendingFile)}
        >
          Încarcă logo
        </Button>
      </Space>
    </div>
  );
}
